import { createGatewaySubscription, getGatewaySubscription, cancelGatewaySubscription, changeGatewayBillingCycle } from '../../../api/subscriptions/subscriptions';
import { tryGetApiToken } from '../../../api/authToken';
import { getDeluxeEnvironment, getSubscriptionDeluxeEnvironment } from './deluxeEnvironment';

const deluxePayload = (subscriptionData) => ({
  name: subscriptionData.name,
  amount: subscriptionData.amount,
  billingCycle: subscriptionData.billingCycle,
  vault: subscriptionData.vault,
  embeddedPayment: subscriptionData.embeddedPayment,
  deluxeEnvironment: getDeluxeEnvironment(),
  trialDays: subscriptionData.trialDays || 0,
  startDate: subscriptionData.startDate,
  metadata: {
    ...subscriptionData.metadata,
    deluxeEnvironment: getDeluxeEnvironment(),
    customerId: subscriptionData.vault?.customerId || subscriptionData.metadata?.customerId,
    paymentMethodId: subscriptionData.vault?.paymentMethodId || subscriptionData.metadata?.paymentMethodId,
    last4: subscriptionData.vault?.last4 || subscriptionData.metadata?.last4,
    isTrialConversion: subscriptionData.metadata?.isTrialConversion || false,
  },
});

export const createSubscription = async (subscriptionData) => {
  try {
    const token = await tryGetApiToken();
    return await createGatewaySubscription(deluxePayload(subscriptionData), token);
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

export const getSubscription = async (subscriptionId, deluxeEnvironment) => {
  try {
    return await getGatewaySubscription({
      subscriptionId,
      deluxeEnvironment: deluxeEnvironment || getDeluxeEnvironment(),
    });
  } catch (error) {
    console.error('Error getting subscription:', error);
    throw error;
  }
};

export const cancelSubscription = async (subscriptionId, reason, deluxeEnvironment) => {
  try {
    return await cancelGatewaySubscription({
      subscriptionId,
      reason,
      deluxeEnvironment: deluxeEnvironment || getDeluxeEnvironment(),
    });
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};

export const renewSubscription = async (renewalData) => {
  try {
    const startDate = new Date().toISOString();
    const token = await tryGetApiToken();
    return await createGatewaySubscription({
      ...deluxePayload({
        ...renewalData,
        metadata: {
          ...renewalData.metadata,
          isRenewal: true,
          previousSubscriptionId: renewalData.previousSubscriptionId,
          renewalStartDate: startDate,
        },
      }),
      startDate,
    }, token);
  } catch (error) {
    console.error('Error renewing subscription:', error);
    throw error;
  }
};

export const upgradeSubscription = async (upgradeData) => {
  try {
    const startDate = new Date().toISOString();
    const token = await tryGetApiToken();
    return await createGatewaySubscription({
      ...deluxePayload({
        ...upgradeData,
        metadata: {
          ...upgradeData.metadata,
          isUpgrade: true,
          isTrialConversion: upgradeData.metadata?.isTrialConversion || false,
          upgradeStartDate: startDate,
        },
      }),
      startDate,
    }, token);
  } catch (error) {
    console.error('Error upgrading subscription:', error);
    throw error;
  }
};

export const changeBillingCycle = async (changeData) => {
  try {
    const startDate = new Date().toISOString();
    return await changeGatewayBillingCycle({
      name: changeData.name,
      currentAmount: changeData.currentAmount,
      currentBillingCycle: changeData.currentBillingCycle,
      newBillingCycle: changeData.newBillingCycle,
      vault: changeData.vault,
      metadata: {
        ...changeData.metadata,
        deluxeEnvironment: getSubscriptionDeluxeEnvironment(changeData.metadata),
        isBillingCycleChange: true,
        changeStartDate: startDate,
      },
    });
  } catch (error) {
    console.error('Error changing billing cycle:', error);
    throw error;
  }
};

export const convertTrialToPaid = async (conversionData) => {
  try {
    const name = conversionData.name?.trim() || 'Customer';
    return await upgradeSubscription({
      ...conversionData,
      name,
      metadata: {
        ...conversionData.metadata,
        isTrialConversion: true,
        previousPackageId: 'free_trial',
        customerName: name,
        taxDetails: {
          baseAmount: conversionData.baseAmount,
          taxAmount: conversionData.taxAmount,
          taxRate: conversionData.taxRate,
          totalAmount: conversionData.amount,
        },
      },
    });
  } catch (error) {
    console.error('Error converting trial to paid subscription:', error);
    throw error;
  }
};
